import React, { useEffect, useState } from 'react';
import {
  PolicyScenario,
  PolicyImpactResults,
  calculatePolicyImpact,
  getDefaultScenario,
  getPredefinedScenarios,
} from '../../services/policyImpactService';
import { getMicromobilityStats, MicromobilityStats } from '../../services/policyImpactService';
import {
  InterventionAreas,
  getDefaultInterventionAreas,
} from '../../types/policyImpact';
import PolicyImpactChart from './PolicyImpactChart';
import ParkingPricingPolicy from './ParkingPricingPolicy';
import VehicleElectrificationPolicy from './VehicleElectrificationPolicy';
import InterventionAreasControl from './InterventionAreasControl';
import './PolicyImpactTool.css';
import { supabaseClient } from '../../services/supabase';

interface PolicyImpactToolProps {
  location: string;
  geofips?: number | null;
}

const PolicyImpactTool: React.FC<PolicyImpactToolProps> = ({ location, geofips = null }) => {
  const [scenario, setScenario] = useState<PolicyScenario>(getDefaultScenario());
  const [results, setResults] = useState<PolicyImpactResults | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [interventionAreas, setInterventionAreas] = useState<InterventionAreas>(getDefaultInterventionAreas());
  const [evMinPercent, setEvMinPercent] = useState<number>(0);
  const [activeChart, setActiveChart] = useState<'line' | 'contributions' | 'forecast' | 'stacked'>('stacked');
  const [targetReductionPercent, setTargetReductionPercent] = useState<number>(80);
  const [targetBaselineYear, setTargetBaselineYear] = useState<number>(2005);
  const [stackedTotals, setStackedTotals] = useState<null | {
    baselineYear: number;
    baselineTotal: number;
    projectedYear: number;
    projectedWithPoliciesTotal: number;
    totalReduction: number;
    percentReduction: number;
  }>(null);
  const [expandedIntervention, setExpandedIntervention] = useState<keyof InterventionAreas | null>(null);
  const [expandedSubPolicy, setExpandedSubPolicy] = useState<string | null>(null);
  const [subPolicyValues, setSubPolicyValues] = useState({
    transitOrientedDevelopment: 0,
    activeTransportation: 0,
    transitImprovements: 0,
    parkingPricing: 0,
    micromobility: 0,
    otherInitiatives: 0,
  });
  const [micromobilityStats, setMicromobilityStats] = useState<MicromobilityStats | null>(null);
  const [micromobilityVMTReductionPercent, setMicromobilityVMTReductionPercent] = useState<number>(0);
  const [transitRevenueMiles, setTransitRevenueMiles] = useState<{ year: number; miles: number } | null>(null);
  const [transitVMTReductionPercent, setTransitVMTReductionPercent] = useState<number>(0);
  const [emfacVersion, setEmfacVersion] = useState<'2021' | '2025'>('2021');

  // Compute parking-pricing-only VMT reduction percent (consistent with service logic)
  const computeParkingVMTReductionPercent = (assumptions: PolicyScenario['parkingPricing']): number => {
    if (assumptions.priceIncreasePercent === 0) return 0;
    const SF_COMMUTE_BY_CAR_PERCENT = 30.29;
    const BASE_ELASTICITY = -0.4;
    if (assumptions.commuteByCarPercent <= 0 || assumptions.commuteByCarPercent >= 100) return 0;
    const elasticity = BASE_ELASTICITY * (SF_COMMUTE_BY_CAR_PERCENT / assumptions.commuteByCarPercent);
    const vmtReductionPercent = Math.abs(elasticity) * (assumptions.priceIncreasePercent / 100);
    const effective = vmtReductionPercent * (assumptions.vmtAffectedPercent / 100) * 100; // percent
    return Math.max(0, Math.min(20, Number(effective.toFixed(1))));
  };

  // Update scenario's parking pricing priceIncreasePercent when menu slider changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      parkingPricing: {
        ...prev.parkingPricing,
        priceIncreasePercent: subPolicyValues.parkingPricing,
      },
    }));
  }, [subPolicyValues.parkingPricing]);

  // Calculate parking pricing and micromobility VMT reduction percentages
  const parkingVMTReductionPercent = computeParkingVMTReductionPercent(scenario.parkingPricing);

  // Update total VMT reduction when calculated percentages change (transit, parking, micromobility)
  // or when direct policy values change
  // When VMT Reduction is expanded, preserve the total and let Other Initiatives adjust
  // When collapsed, set total = sum of policy reductions when policies change
  useEffect(() => {
    // Recalculate parking VMT reduction using the latest parking pricing value
    // Use subPolicyValues.parkingPricing directly to avoid stale values
    const currentParkingVMT = computeParkingVMTReductionPercent({
      ...scenario.parkingPricing,
      priceIncreasePercent: subPolicyValues.parkingPricing,
    });
    
    const sumOfPolicyReductions = 
      (subPolicyValues.transitOrientedDevelopment || 0) +
      (subPolicyValues.activeTransportation || 0) +
      transitVMTReductionPercent +
      currentParkingVMT +
      micromobilityVMTReductionPercent;
    
    // If VMT Reduction is expanded, preserve the total unless Other Initiatives would go to zero or negative
    // In that case, decrease the total to match the sum of policy reductions
    if (expandedIntervention === 'vmtReduction') {
      setInterventionAreas(prev => {
        const currentTotal = prev.vmtReduction;
        const calculatedOtherInitiatives = currentTotal - sumOfPolicyReductions;
        
        // If Other Initiatives would be zero or negative, decrease total to match sum
        if (calculatedOtherInitiatives <= 0) {
          const newTotal = Math.max(0, Math.min(20, sumOfPolicyReductions));
          if (Math.abs(currentTotal - newTotal) > 0.01) {
            return {
              ...prev,
              vmtReduction: newTotal,
            };
          }
        }
        // Otherwise, preserve the total so Other Initiatives can adjust
        return prev;
      });
      return;
    }
    
    // When collapsed and policies change, set total to sum of policy reductions
    // This ensures the total reflects the active policies
    const newTotal = Math.max(0, Math.min(20, sumOfPolicyReductions));
    
    setInterventionAreas(prev => {
      // Only update if the new total is different from current
      // This prevents unnecessary updates and ensures smooth slider behavior
      if (Math.abs(prev.vmtReduction - newTotal) > 0.01) {
        return {
          ...prev,
          vmtReduction: newTotal,
        };
      }
      return prev;
    });
  }, [scenario.parkingPricing, subPolicyValues.parkingPricing, transitVMTReductionPercent, micromobilityVMTReductionPercent, subPolicyValues.transitOrientedDevelopment, subPolicyValues.activeTransportation, expandedIntervention]);

  // Update scenario's vmtReductionPercent when interventionAreas.vmtReduction changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      vmtReductionPercent: interventionAreas.vmtReduction,
    }));
  }, [interventionAreas.vmtReduction]);

  // Update Other Initiatives when the main VMT Reduction slider is manually changed
  // (This handles the case where the user directly adjusts the top-level slider)
  // When expanded, also update when policies change (so Other Initiatives adjusts automatically)
  useEffect(() => {
    // Recalculate parking VMT reduction inside effect to ensure we have the latest value
    const currentParkingVMT = computeParkingVMTReductionPercent(scenario.parkingPricing);
    
    const sumOfPolicyReductions = 
      (subPolicyValues.transitOrientedDevelopment || 0) +
      (subPolicyValues.activeTransportation || 0) +
      transitVMTReductionPercent +
      currentParkingVMT +
      micromobilityVMTReductionPercent;
    
    const calculatedOtherInitiatives = Math.max(0, interventionAreas.vmtReduction - sumOfPolicyReductions);
    
    // Only update if it's different to avoid loops
    // When expanded, this ensures Other Initiatives updates when policies change
    // When collapsed, this ensures Other Initiatives updates when total changes
    setSubPolicyValues(prev => {
      if (Math.abs((prev.otherInitiatives || 0) - calculatedOtherInitiatives) > 0.01) {
        return {
          ...prev,
          otherInitiatives: calculatedOtherInitiatives,
        };
      }
      return prev;
    });
  }, [interventionAreas.vmtReduction, subPolicyValues.transitOrientedDevelopment, subPolicyValues.activeTransportation, scenario.parkingPricing, transitVMTReductionPercent, micromobilityVMTReductionPercent]);

  // Sync sub-policy values with scenario's VMT reduction components
  // Note: transitImprovements is NOT synced here because subPolicyValues.transitImprovements
  // stores the service increase % (0-50%), while scenario.vmtReductionComponents.transitImprovements
  // stores the calculated VMT reduction % (0-10%). They are different units.
  useEffect(() => {
    setSubPolicyValues(prev => ({
      ...prev,
      transitOrientedDevelopment: scenario.vmtReductionComponents.transitOrientedDevelopment,
      activeTransportation: scenario.vmtReductionComponents.activeTransportation,
      // transitImprovements is NOT synced - it's the service increase %, not VMT reduction %
    }));
  }, [scenario.vmtReductionComponents.transitOrientedDevelopment, scenario.vmtReductionComponents.activeTransportation]);

  // Sync sub-policy values back to scenario when they change
  // Note: transitImprovements is handled separately above (stores calculated VMT %)
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      vmtReductionComponents: {
        ...prev.vmtReductionComponents,
        transitOrientedDevelopment: subPolicyValues.transitOrientedDevelopment,
        activeTransportation: subPolicyValues.activeTransportation,
        // transitImprovements is handled in the transit service calculation effect above
      },
    }));
  }, [subPolicyValues.transitOrientedDevelopment, subPolicyValues.activeTransportation]);

  // Keep micromobility scale in scenario when slider changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      micromobility: {
        ...(prev.micromobility || {
          yearImplemented: 2025,
          tripsPerResident: 3,
          percentBike: 60,
          percentScooter: 40,
          milesPerBikeTrip: 1.61,
          milesPerScooterTrip: 1.2,
          percentBikeTripsDisplaceCars: 30,
          percentScooterTripsDisplaceCars: 20,
          kwhPerMileBike: 0.02,
          kwhPerMileScooter: 0.04,
          scalePercent: 0,
        }),
        scalePercent: subPolicyValues.micromobility || 0,
      },
    }));
  }, [subPolicyValues.micromobility]);

  // Compute Transit Service Improvements -> VMT reduction percent
  useEffect(() => {
    // Parameters (can be wired to live sources later)
    const elasticity = 0.7; // Handy et al. 2013
    const transitModeShare = 20; // % placeholder
    const statewideModeShift = 57.8; // % FHWA 2017
    const increasePct = subPolicyValues.transitImprovements || 0; // % increase in service
    const vmtReductionPct = (elasticity * (increasePct / 100) * (transitModeShare / 100) * (statewideModeShift / 100)) * 100;
    const pctRounded = Math.max(0, Number(vmtReductionPct.toFixed(3)));
    setTransitVMTReductionPercent(pctRounded);
    
    // Store the calculated VMT reduction % in the scenario (not the service increase %)
    setScenario(prev => ({
      ...prev,
      vmtReductionComponents: {
        ...prev.vmtReductionComponents,
        transitImprovements: pctRounded, // Store calculated VMT reduction %, not service increase %
      },
    }));
  }, [subPolicyValues.transitImprovements]);

  // Load micromobility detailed stats whenever scenario/location changes
  useEffect(() => {
    const loadStats = async () => {
      if (!location) return;
      try {
        const stats = await getMicromobilityStats(location, {
          ...scenario,
          micromobility: {
            ...(scenario.micromobility || {
              yearImplemented: 2025,
              tripsPerResident: 3,
              percentBike: 60,
              percentScooter: 40,
              milesPerBikeTrip: 1.61,
              milesPerScooterTrip: 1.2,
              percentBikeTripsDisplaceCars: 30,
              percentScooterTripsDisplaceCars: 20,
              kwhPerMileBike: 0.02,
              kwhPerMileScooter: 0.04,
              scalePercent: 0,
            }),
            scalePercent: subPolicyValues.micromobility || 0,
          }
        } as PolicyScenario, emfacVersion);
        setMicromobilityStats(stats);
        
        // Calculate VMT reduction percentage from displaced car miles
        if (stats.displacedCarMiles > 0 && results?.baseline?.vmt) {
          const vmtReductionPercent = (stats.displacedCarMiles / results.baseline.vmt) * 100;
          setMicromobilityVMTReductionPercent(Math.max(0, Math.min(20, Number(vmtReductionPercent.toFixed(1)))));
        } else {
          setMicromobilityVMTReductionPercent(0);
        }
      } catch (e) {
        console.warn('Failed to load micromobility stats', e);
        setMicromobilityStats(null);
        setMicromobilityVMTReductionPercent(0);
      }
    };
    if (results?.baseline?.vmt) {
      loadStats();
    }
  }, [location, scenario, subPolicyValues.micromobility, results?.baseline?.vmt, emfacVersion]);

  // Load baseline transit revenue miles (vehicle revenue miles) for the selected location
  useEffect(() => {
    const loadTransitMiles = async () => {
      try {
        // Prefer geofips if provided; fallback to searching by name
        let row: any | null = null;
        if (geofips) {
          const { data } = await supabaseClient
            .from('vehicle_revenue_miles')
            .select('*')
            .eq('geofips', String(geofips))
            .maybeSingle();
          row = data || null;
        }
        if (!row) {
          const { data } = await supabaseClient
            .from('vehicle_revenue_miles')
            .select('*')
            .ilike('geo_name', `%${location}%`)
            .in('location_type', ['City', 'Place'])
            .limit(1)
            .maybeSingle();
          row = data || null;
        }

        if (row) {
          // Choose the latest available year up to 2023
          const possibleYears = [
            2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016,
            2015, 2014, 2013, 2012, 2011, 2010,
          ];
          for (const yr of possibleYears) {
            const val = Number(row[String(yr)] || 0);
            if (val > 0) {
              setTransitRevenueMiles({ year: yr, miles: val });
              return;
            }
          }
        }
        setTransitRevenueMiles(null);
      } catch (e) {
        console.warn('Failed to load vehicle_revenue_miles for', location, e);
        setTransitRevenueMiles(null);
      }
    };
    loadTransitMiles();
  }, [location, geofips]);

  // Initialize vehicleElectrification with current EV percentage when baseline data loads (only once)
  useEffect(() => {
    if (results?.baseline.currentEVPercent !== undefined) {
      const currentEV = results.baseline.currentEVPercent;
      setEvMinPercent(currentEV);
      setInterventionAreas(prev => {
        // Only update if different to avoid unnecessary updates
        if (prev.vehicleElectrification !== currentEV) {
          return {
            ...prev,
            vehicleElectrification: currentEV,
          };
        }
        return prev;
      });
      // Also set evAdoptionPercent to match
      setScenario(prev => {
        if (prev.evAdoptionPercent !== currentEV) {
          return {
            ...prev,
            evAdoptionPercent: currentEV,
          };
        }
        return prev;
      });
    }
  }, [results?.baseline.currentEVPercent]);

  // Default grid decarbonization lever is now the clean electricity year (SB 100 default: 2045).
  useEffect(() => {
    setInterventionAreas((prev) => ({
      ...prev,
      gridDecarbonization: prev.gridDecarbonization || 2045,
    }));
    setScenario((prev) => ({
      ...prev,
      gridCleanElectricityYear: (prev as any).gridCleanElectricityYear || 2045,
    } as any));
  }, []);

  // Sync vehicleElectrification slider with evAdoptionPercent
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      // Do not allow going below the current EV share.
      evAdoptionPercent: Math.max(evMinPercent || 0, interventionAreas.vehicleElectrification),
    }));
  }, [interventionAreas.vehicleElectrification, evMinPercent]);

  // Sync Energy Efficiency slider (cap at 40%) with scenario
  useEffect(() => {
    const eff = Math.max(0, Math.min(40, interventionAreas.energyEfficiency || 0));
    setScenario(prev => ({
      ...prev,
      energyEfficiencyPercent: eff,
    }));
  }, [interventionAreas.energyEfficiency]);

  // Sync Building Electrification slider with scenario heatPumpAdoptionPercent (NG reduction; electricity impact later)
  useEffect(() => {
    const bldgElec = Math.max(0, Math.min(100, interventionAreas.buildingsElectrification || 0));
    setScenario(prev => ({
      ...prev,
      heatPumpAdoptionPercent: bldgElec,
    }));
  }, [interventionAreas.buildingsElectrification]);

  // Sync Organics Diversion slider with scenario organicsDiversionPercent (50-100%)
  useEffect(() => {
    const organics = Math.max(50, Math.min(100, interventionAreas.organicsDiversion || 50));
    setScenario(prev => ({
      ...prev,
      organicsDiversionPercent: organics,
    }));
  }, [interventionAreas.organicsDiversion]);

  // Sync Grid Decarbonization slider (year) with scenario.gridCleanElectricityYear
  useEffect(() => {
    const raw = Number(interventionAreas.gridDecarbonization || 2045);
    const clamped = Math.max(2025, Math.min(2045, raw));
    setScenario(prev => ({
      ...prev,
      gridCleanElectricityYear: clamped,
    } as any));
  }, [interventionAreas.gridDecarbonization]);

  // Calculate impact whenever scenario or location changes
  // Add a ref to track if we're currently calculating to prevent loops
  const calculatingRef = React.useRef(false);
  
  useEffect(() => {
    const calculateImpact = async () => {
      if (!location) return;
      
      // Prevent concurrent calculations
      if (calculatingRef.current) {
        console.log('PolicyImpactTool: Calculation already in progress, skipping...');
        return;
      }

      calculatingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const impact = await calculatePolicyImpact(location, scenario, emfacVersion);
        setResults(impact);
      } catch (err) {
        console.error('Error calculating policy impact:', err);
        setError('Failed to calculate policy impact. Please try again.');
        // Don't set results to null on error to avoid clearing previous data
      } finally {
        setLoading(false);
        calculatingRef.current = false;
      }
    };

    // Debounce calculations to avoid too many updates
    const timeoutId = setTimeout(calculateImpact, 500);
    return () => {
      clearTimeout(timeoutId);
      calculatingRef.current = false;
    };
  }, [location, scenario, emfacVersion]);

  const handleLoadPredefinedScenario = (scenarioName: string) => {
    const predefined = getPredefinedScenarios();
    if (predefined[scenarioName]) {
      const loadedScenario = predefined[scenarioName];
      setScenario(loadedScenario);
      // Also update interventionAreas so the sliders move
      setInterventionAreas({
        urbanInfill: 0, // Not used in scenarios
        vmtReduction: loadedScenario.vmtReductionPercent,
        vehicleElectrification: loadedScenario.evAdoptionPercent,
        energyEfficiency: loadedScenario.energyEfficiencyPercent,
        buildingsElectrification: loadedScenario.heatPumpAdoptionPercent,
        gridDecarbonization: loadedScenario.gridCleanElectricityYear,
        organicsDiversion: loadedScenario.organicsDiversionPercent,
      });
      // Update sub-policy values for VMT components
      setSubPolicyValues(prev => ({
        ...prev,
        transitOrientedDevelopment: loadedScenario.vmtReductionComponents.transitOrientedDevelopment,
        activeTransportation: loadedScenario.vmtReductionComponents.activeTransportation,
        transitImprovements: loadedScenario.vmtReductionComponents.transitImprovements,
        parkingPricing: loadedScenario.parkingPricing.priceIncreasePercent,
        micromobility: loadedScenario.micromobility?.scalePercent || 0,
      }));
    }
  };

  const handleResetScenario = () => {
    const defaultScenario = getDefaultScenario();
    setScenario(defaultScenario);
    // Also reset interventionAreas so the sliders move
    setInterventionAreas({
      urbanInfill: 0,
      vmtReduction: defaultScenario.vmtReductionPercent,
      vehicleElectrification: defaultScenario.evAdoptionPercent,
      energyEfficiency: defaultScenario.energyEfficiencyPercent,
      buildingsElectrification: defaultScenario.heatPumpAdoptionPercent,
      gridDecarbonization: defaultScenario.gridCleanElectricityYear,
      organicsDiversion: defaultScenario.organicsDiversionPercent,
    });
    // Reset sub-policy values
    setSubPolicyValues({
      transitOrientedDevelopment: defaultScenario.vmtReductionComponents.transitOrientedDevelopment,
      activeTransportation: defaultScenario.vmtReductionComponents.activeTransportation,
      transitImprovements: defaultScenario.vmtReductionComponents.transitImprovements,
      parkingPricing: defaultScenario.parkingPricing.priceIncreasePercent,
      micromobility: defaultScenario.micromobility?.scalePercent || 0,
      otherInitiatives: 0,
    });
  };

  return (
    <div className="policy-impact-tool">
      {error && (
        <div className="policy-tool-error">
          <p>{error}</p>
        </div>
      )}

      <div className="policy-tool-content">
        {/* GHG Forecast: clean layout with chart left, metrics right */}
        <section className="policy-main-forecast">
          <div className="policy-main-forecast-header">
            <h2 className="section-title">GHG Forecast - {location}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <label htmlFor="emfac-version-select" style={{ fontSize: '0.8125rem', color: '#6b7280', fontWeight: 500 }}>
                  EMFAC Version:
                </label>
                <select
                  id="emfac-version-select"
                  value={emfacVersion}
                  onChange={(e) => {
                    setEmfacVersion(e.target.value as '2021' | '2025');
                    // Clear baseline cache when version changes
                    import('../../services/policyBaselineCache').then(({ clearBaselineCache }) => {
                      clearBaselineCache();
                    });
                  }}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontWeight: 500,
                    color: '#1f2937'
                  }}
                >
                  <option value="2025">EMFAC2025</option>
                  <option value="2021">EMFAC2021</option>
                </select>
              </div>
              {activeChart === 'stacked' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Target:</span>
                  <select
                    value={targetReductionPercent}
                    onChange={(e) => setTargetReductionPercent(Number(e.target.value))}
                    style={{
                      padding: '0.35rem 0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {[0, 40, 50, 60, 70, 80, 90, 100].map(val => (
                      <option key={val} value={val}>{val}%</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>below</span>
                  <select
                    value={targetBaselineYear}
                    onChange={(e) => setTargetBaselineYear(Number(e.target.value))}
                    style={{
                      padding: '0.35rem 0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {[2005, 2010, 2015, 2020, 2023].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>levels</span>
                </div>
              )}
              <div className="chart-type-buttons">
              <button
                className={`chart-type-button ${activeChart === 'stacked' ? 'active' : ''}`}
                onClick={() => setActiveChart('stacked')}
              >
                Stacked
              </button>
              <button
                className={`chart-type-button ${activeChart === 'line' ? 'active' : ''}`}
                onClick={() => setActiveChart('line')}
              >
                Line Chart
              </button>
              <button
                className={`chart-type-button ${activeChart === 'contributions' ? 'active' : ''}`}
                onClick={() => setActiveChart('contributions')}
              >
                Policies
              </button>
              </div>
            </div>
          </div>

          <div className="policy-main-forecast-layout">
            {results && (
              <div className="policy-main-metrics">
                <div className="impact-metric-card baseline">
                  <div className="metric-label">Current Baseline</div>
                  <div className="metric-value">
                    <span className="metric-number">
                      {(stackedTotals?.baselineTotal ?? results.baseline.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card projected">
                  <div className="metric-label">Projected with Policies</div>
                  <div className="metric-value">
                    <span className="metric-number">
                      {(stackedTotals?.projectedWithPoliciesTotal ?? results.projected.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card reduction">
                  <div className="metric-label">Total Reduction</div>
                  <div className="metric-value reduction-value">
                    <span className="metric-number">
                      {(stackedTotals?.totalReduction ?? results.reductions.totalReduction).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card percentage">
                  <div className="metric-label">Percent Reduction</div>
                  <div className="metric-value reduction-value">
                    <span className="metric-number">
                      {(stackedTotals?.percentReduction ?? results.reductions.percentReduction).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="policy-main-chart">
              <PolicyImpactChart 
                results={results} 
                loading={loading} 
                scenario={scenario} 
                location={location} 
                chartType={activeChart}
                targetReductionPercent={targetReductionPercent}
                targetBaselineYear={targetBaselineYear}
                emfacVersion={emfacVersion}
                onTotalsChange={setStackedTotals}
              />
            </div>
          </div>
        </section>

        {/* Implementation by 2045 Section - Full Width Below */}
        <div className="policy-intervention-areas-section">
            <InterventionAreasControl 
              interventionAreas={interventionAreas}
              onChange={setInterventionAreas}
              onExpandedChange={setExpandedIntervention}
              onSubPolicyExpand={setExpandedSubPolicy}
              subPolicyValues={subPolicyValues}
              onSubPolicyChange={setSubPolicyValues}
              parkingVMTReductionPercent={parkingVMTReductionPercent}
              computeParkingVMTReductionPercent={(priceIncreasePercent: number) => 
                computeParkingVMTReductionPercent({
                  ...scenario.parkingPricing,
                  priceIncreasePercent,
                })
              }
              micromobilityVMTReductionPercent={micromobilityVMTReductionPercent}
              transitVMTReductionPercent={transitVMTReductionPercent}
              vehicleElectrificationMinPercent={evMinPercent}
              onLoadScenario={handleLoadPredefinedScenario}
              onResetScenario={handleResetScenario}
            />
          </div>

        {/* Expanded Intervention Details */}
        {expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'parkingPricing' && (
          <div id="parking-pricing-details" className="policy-intervention-details-section">
            <h3 className="intervention-details-title">Parking Pricing Policy</h3>
            <div className="intervention-details-content">
              <ParkingPricingPolicy
                assumptions={{
                  ...scenario.parkingPricing,
                  priceIncreasePercent: subPolicyValues.parkingPricing,
                }}
                onChange={(updatedAssumptions) => {
                  // Update scenario assumptions
                  setScenario(prev => ({
                    ...prev,
                    parkingPricing: updatedAssumptions,
                  }));

                  // Keep sub-policy slider in sync
                  setSubPolicyValues(prev => ({
                    ...prev,
                    parkingPricing: updatedAssumptions.priceIncreasePercent,
                  }));

                  // Update the top-level VMT Reduction slider: sum of all policies + otherInitiatives
                  const computedParking = computeParkingVMTReductionPercent(updatedAssumptions);
                  const sumOfPolicyReductions = 
                    (subPolicyValues.transitOrientedDevelopment || 0) +
                    (subPolicyValues.activeTransportation || 0) +
                    transitVMTReductionPercent +
                    computedParking +
                    micromobilityVMTReductionPercent;
                  setInterventionAreas(prev => ({
                    ...prev,
                    vmtReduction: Math.max(0, Math.min(20, sumOfPolicyReductions + (subPolicyValues.otherInitiatives || 0))),
                  }));
                }}
                currentVMT={results?.baseline.vmt || 0}
                projectedBAUVMT={results?.baseline.vmt || 0}
                location={location}
                geofips={geofips}
              />
            </div>
          </div>
        )}

        {expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'micromobility' && (
            <div id="micromobility-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Expand Micromobility</h3>
              <div className="intervention-details-content">
                {/* Top full-width control mirroring the menu slider */}
                <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
                  <div className="policy-main-label">Expand micromobility by</div>
                  <div className="policy-main-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={200}
                      step={5}
                      value={subPolicyValues.micromobility}
                      onChange={(e) => setSubPolicyValues(prev => ({ ...prev, micromobility: Number(e.target.value) }))}
                      className="policy-main-slider"
                    />
                    <div className="policy-main-value">{subPolicyValues.micromobility}%</div>
                  </div>
                </div>
                <p>
                  This measure estimates reduced GHG emissions from trips shifted to shared e-bikes and e-scooters and
                  subtracts added electricity emissions. Default assumptions reflect findings that roughly one-third of
                  micromobility trips displace motor vehicle trips. Users can scale program intensity with the slider in the menu.
                </p>

                {micromobilityStats && (
                  <div className="policy-results-grid">
                    <div className="result-item">
                      <span className="result-label">Population:</span>
                      <span className="result-value">{micromobilityStats.population.toLocaleString()}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Program Scale:</span>
                      <span className="result-value">{micromobilityStats.scalePercent}%</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Trips per Resident:</span>
                      <span className="result-value">{micromobilityStats.tripsPerResident}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Total Program Trips:</span>
                      <span className="result-value">{micromobilityStats.totalProgramTrips.toLocaleString()}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Annual Bike Miles:</span>
                      <span className="result-value">{micromobilityStats.bikeMiles.toLocaleString()}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Annual Scooter Miles:</span>
                      <span className="result-value">{micromobilityStats.scooterMiles.toLocaleString()}</span>
                    </div>
                    <div className="result-item highlight">
                      <span className="result-label">VMT Reduced:</span>
                      <span className="result-value">{micromobilityStats.displacedCarMiles.toLocaleString()} miles</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">EV Bike+Scooter Electricity:</span>
                      <span className="result-value">{micromobilityStats.electricityKwh.toLocaleString()} kWh</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Tons Avoided from Cars:</span>
                      <span className="result-value">{micromobilityStats.avoidedCarTons.toLocaleString()}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Tons Added from Electricity:</span>
                      <span className="result-value">{micromobilityStats.electricityTons.toLocaleString()}</span>
                    </div>
                    <div className="result-item highlight">
                      <span className="result-label">Net GHG Reduction:</span>
                      <span className="result-value">{micromobilityStats.netTons.toLocaleString()} MT CO₂e</span>
                    </div>
                  </div>
                )}

                {!micromobilityStats && (
                  <p style={{ opacity: 0.7 }}>Loading micromobility statistics…</p>
                )}
              </div>
            </div>
          )}

        {expandedIntervention === 'vehicleElectrification' && (
          <div id="vehicle-electrification-details" className="policy-intervention-details-section">
            <h3 className="intervention-details-title">Vehicle Electrification Policy</h3>
            <div className="intervention-details-content">
              <VehicleElectrificationPolicy
                emfacVersion={emfacVersion}
                currentEVPercent={results?.baseline.currentEVPercent || 0}
                targetEVPercent={interventionAreas.vehicleElectrification}
                location={location}
                results={results}
              />
            </div>
        </div>
        )}

        {expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'transitImprovements' && (
          <div id="transit-service-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
            <h3 className="intervention-details-title">Extend Transit Network Coverage of Miles</h3>
            <div className="intervention-details-content">
              {/* Top full-width control mirroring the menu slider */}
              <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
                <div className="policy-main-label">Expand transit service by</div>
                <div className="policy-main-slider-container">
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={1}
                    value={subPolicyValues.transitImprovements}
                    onChange={(e) => setSubPolicyValues(prev => ({ ...prev, transitImprovements: Number(e.target.value) }))}
                    className="policy-main-slider"
                  />
                  <div className="policy-main-value">{subPolicyValues.transitImprovements}%</div>
                </div>
              </div>

              <p>
                This measure will expand the local transit network by either adding or modifying existing service or
                extending operating hours near the project area. Earlier starts and later end times can accommodate
                alternative-shift workers, encouraging transit use and reducing VMT and associated GHG emissions.
              </p>

              {(() => {
                const percentIncrease = subPolicyValues.transitImprovements || 0; // %
                const transitModeShare = 20; // % (placeholder until wired to Census)
                const elasticity = 0.7; // Handy et al. 2013
                const statewideModeShift = 57.8; // % FHWA 2017
                const vmtReductionPct = (elasticity * (percentIncrease / 100) * (transitModeShare / 100) * (statewideModeShift / 100)) * 100;
                const vmtReductionPctRounded = Math.max(0, Number(vmtReductionPct.toFixed(3)));
                const yearsTo2045 = 2045 - 2025 + 1;
                const ldvBaseline = results?.baseline.lightDutyGHG || 0;
                const estGHGReduction2045 = Math.round(ldvBaseline * yearsTo2045 * (vmtReductionPctRounded / 100));
                const baselineServiceMiles = transitRevenueMiles?.miles;
                const baselineYear = transitRevenueMiles?.year;
                const totalVmt = results?.baseline.vmt || 0;
                const vmtReducedMiles = Math.round(totalVmt * (vmtReductionPctRounded / 100));
                return (
                  <div>
                    <p style={{ marginTop: 8 }}>
                      <strong>Smart narrative:</strong> {location}
                      {baselineServiceMiles ? ` has ${baselineServiceMiles.toLocaleString()} transit service miles in ${baselineYear}.` : ''}
                      {` If ${location} increases transit service miles by ${percentIncrease}%, total GHG emissions from light-duty vehicles by 2045 are estimated to be reduced by ${estGHGReduction2045.toLocaleString()} tCO₂e.`}
                    </p>
                    <div className="policy-results-grid" style={{ marginTop: 12 }}>
                      <div className="result-item"><span className="result-label">Elasticity:</span><span className="result-value">{elasticity}</span></div>
                      <div className="result-item"><span className="result-label">Transit mode share:</span><span className="result-value">{transitModeShare}%</span></div>
                      <div className="result-item"><span className="result-label">Statewide mode shift factor:</span><span className="result-value">{statewideModeShift}%</span></div>
                      <div className="result-item highlight"><span className="result-label">VMT reduction in year of implementation:</span><span className="result-value">{vmtReductionPctRounded.toFixed(3)}%</span></div>
                      <div className="result-item highlight"><span className="result-label">Total VMT reduced (year of implementation):</span><span className="result-value">{vmtReducedMiles.toLocaleString()} miles</span></div>
                    </div>
                    <p style={{ marginTop: 8, opacity: 0.75 }}>
                      Sources: FHWA 2017; Handy et al. 2013.
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyImpactTool;
